import { useEffect } from "react";
import {
  Alert,
  Avatar,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { useCalculateShipmentPriceMutation } from "src/_api/mapitShipApi";
import { omitBy } from "lodash";
import { useSelector } from "react-redux";
import { DEFAULT_SHIPMENT_PAYLOAD } from "../../shipmentDefaults";

// Same list the per-order carrier list offers.
const SERVICE_TYPE = [
  "express",
  "sameDay",
  "coldDelivery",
  "heavyAndBulky",
  "lockerDelivery",
  "pudo",
  "bullet",
];

const sameCompany = (a, b) =>
  !!a &&
  !!b &&
  String(a.deliveryOptionId) === String(b.deliveryOptionId) &&
  a.integrationType === b.integrationType;

/**
 * Step 1 of the bulk wizard: pick ONE carrier.
 *
 * Deliberately no price column: the catalog is fetched with a generic probe
 * shipment, so its prices would not be this batch's prices. Availability and the
 * real per-order price are resolved in the next step.
 */
export default function BulkCarrierPicker({
  selectedCompany,
  onSelectCompany,
  serviceType,
  setServiceType,
}) {
  const { t } = useTranslation();
  const isDownMdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { profile } = useSelector((state) => state.entities.profile);

  const [calculateShipmentPrice, { data: companies, isLoading }] =
    useCalculateShipmentPriceMutation();

  useEffect(() => {
    calculateShipmentPrice(
      omitBy(
        { ...DEFAULT_SHIPMENT_PAYLOAD, serviceType },
        (value) => value === "" || value === undefined || value === null,
      ),
    );
  }, [calculateShipmentPrice, serviceType]);

  const handleWhatsAppClick = () => {
    const phone =
      profile?.merchant?.currentStatus === "WAITING"
        ? "966581849999"
        : "966597327471";
    window.open(`https://api.whatsapp.com/send/?phone=${phone}`, "_blank");
  };

  const list = companies?.deliveryCompany ?? [];

  return (
    <Box>
      <TextField
        sx={{ width: { xs: "100%", sm: "60%", md: "40%" }, mb: 2 }}
        select
        label={t("Service Type")}
        size="small"
        value={serviceType}
        onChange={(event) => setServiceType(event.target.value)}
        InputProps={{
          endAdornment: serviceType ? (
            <InputAdornment position="end" sx={{ pr: 2 }}>
              <IconButton size="small" onClick={() => setServiceType("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      >
        {SERVICE_TYPE.map((type) => (
          <MenuItem key={type} value={type}>
            {t(`serviceType.${type}`)}
          </MenuItem>
        ))}
      </TextField>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t("bulkAssign.carrierPickerHint")}
      </Alert>

      {/* header row */}
      <Grid container gap={1} alignItems="center" sx={{ py: 1, px: 1 }}>
        <Grid xs={12} md={3.6} item>
          <Typography variant="subtitle2" fontWeight="bold">
            {t("Courier")}
          </Typography>
        </Grid>
        {!isDownMdScreen && (
          <>
            <Grid md={1.8} item>
              <Typography variant="subtitle2" fontWeight="bold">
                {t("Serivce type")}
              </Typography>
            </Grid>
            <Grid md={1.8} item>
              <Typography variant="subtitle2" fontWeight="bold">
                {t("Delivery time")}
              </Typography>
            </Grid>
            <Grid md={2} item>
              <Typography variant="subtitle2" fontWeight="bold">
                {t("Pickedup Methods")}
              </Typography>
            </Grid>
            <Grid md={1.6} item>
              <Typography variant="subtitle2" fontWeight="bold">
                {t("Delivery Method")}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>

      {isLoading &&
        Array.from({ length: 4 }).map((_, index) => (
          <Grid
            key={index}
            container
            gap={1}
            alignItems="center"
            sx={{ py: 1.5, px: 1 }}
          >
            <Grid xs={12} md={3.6} item>
              <Skeleton variant="text" width="70%" height={24} />
            </Grid>
            {!isDownMdScreen && (
              <>
                <Grid md={1.8} item>
                  <Skeleton variant="text" width="80%" height={24} />
                </Grid>
                <Grid md={1.8} item>
                  <Skeleton variant="text" width="70%" height={24} />
                </Grid>
                <Grid md={2} item>
                  <Skeleton variant="text" width="80%" height={24} />
                </Grid>
                <Grid md={1.6} item>
                  <Skeleton variant="text" width="80%" height={24} />
                </Grid>
              </>
            )}
          </Grid>
        ))}

      {!isLoading &&
        list.map((company) => {
          const checked = sameCompany(company, selectedCompany);
          return (
            <Box
              key={`${company?.integrationType}-${company?.deliveryOptionId}`}
              onClick={() => onSelectCompany(company)}
              bgcolor={checked ? "action.selected" : "background.paper"}
              borderBottom="1px solid #0000001f"
              sx={{
                cursor: "pointer",
                borderRadius: 1,
                "&:hover": { backgroundColor: "#F9FAFB" },
              }}
            >
              <Grid container gap={1} alignItems="center" sx={{ py: 1, px: 1 }}>
                <Grid xs={12} md={3.6} item>
                  <Box display="flex" alignItems="center">
                    <Radio
                      size="small"
                      checked={checked}
                      onChange={() => onSelectCompany(company)}
                      inputProps={{
                        "aria-label": company?.deliveryOptionName,
                      }}
                    />
                    <Avatar
                      sx={{ width: 40, height: 40, mr: 1 }}
                      variant="square"
                      src={company?.logo}
                    />
                    <Tooltip
                      title={company?.deliveryOptionName}
                      placement="top"
                    >
                      <Typography variant="subtitle2" sx={{ maxWidth: "100%" }}>
                        {company?.deliveryOptionName}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Grid>
                {!isDownMdScreen && (
                  <>
                    <Grid md={1.8} item>
                      <Tooltip title={company?.serviceType} placement="top">
                        <Typography noWrap variant="subtitle2">
                          {company?.serviceType}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid md={1.8} item>
                      <Tooltip title={company?.avgDeliveryTime} placement="top">
                        <Typography noWrap variant="subtitle2">
                          {company?.avgDeliveryTime}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid md={2} item>
                      <Tooltip title={company?.pickupDropoff} placement="top">
                        <Typography noWrap variant="subtitle2">
                          {company?.pickupDropoff}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid md={1.6} item>
                      <Tooltip title={company?.deliveryType} placement="top">
                        <Typography noWrap variant="subtitle2">
                          {company?.deliveryType}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          );
        })}

      {!isLoading && !list.length && (
        <>
          <Typography
            textAlign="center"
            variant="body2"
            color="secondary"
            sx={{ mt: 8, mb: 3 }}
          >
            {t("No delivery companies found")}
          </Typography>
          <Typography textAlign="center" variant="body2" color="primary.main">
            {t(
              "To help us find shipping companies that support this city, please",
            )}{" "}
            <Typography
              component="span"
              onClick={handleWhatsAppClick}
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {t("contact us via WhatsApp")}
            </Typography>
          </Typography>
        </>
      )}
    </Box>
  );
}
